import { useCallback, useRef } from "react";
import { toast } from "@/hooks/use-toast";

export interface EditClaimPopupOptions {
  claimId: string;
  claim: any;
  onSaved?: () => void;
}

const SESSION_KEY_PREFIX = "edit_claim_popup_";

export function useEditClaimPopup() {
  const popupRef = useRef<Window | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const openEditClaim = useCallback((options: EditClaimPopupOptions) => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    const sessionKey = `${SESSION_KEY_PREFIX}${Date.now()}`;
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify({ claim: options.claim }));
    } catch {
      toast({
        title: "Storage Unavailable",
        description: "Unable to open the edit form. Please allow storage access and try again.",
        variant: "destructive",
      });
      return;
    }

    const params = new URLSearchParams();
    params.set("session_key", sessionKey);
    params.set("claim_id", options.claimId);

    const width = 860;
    const height = 820;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

    const popup = window.open(
      `/edit-claim?${params.toString()}`,
      "edit-claim",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup || popup.closed) {
      sessionStorage.removeItem(sessionKey);
      toast({
        title: "Popup Blocked",
        description: "Please allow popups for this site to open the edit form.",
        variant: "destructive",
      });
      return;
    }

    popupRef.current = popup;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "claim-saved") return;
      options.onSaved?.();
    };

    window.addEventListener("message", onMessage);

    const pollId = setInterval(() => {
      if (popup.closed) cleanup();
    }, 500);

    function cleanup() {
      window.removeEventListener("message", onMessage);
      clearInterval(pollId);
      sessionStorage.removeItem(sessionKey);
      popupRef.current = null;
      cleanupRef.current = null;
    }

    cleanupRef.current = cleanup;
  }, []);

  return { openEditClaim };
}
